import Penguin from './Penguin'


/**
 * NPCPenguin - Non-Player Character penguin
 * 
 * NPCs are special penguins that:
 * - Cannot be added as buddies
 * - Don't have player cards
 * - Open NPC dialog when clicked instead
 * - Can give quests and rewards
 * - Are controlled by the server/admin dashboard
 */
export default class NPCPenguin extends Penguin {

    constructor(npcData, room) {
        // Use provided display name/username if available, otherwise fallback to internal name
        // The server sends the public name in 'username' and 'displayName' fields
        const name = npcData.displayName || npcData.username || npcData.name || npcData.internalName || 'NPC'

        // Format NPC data to match user structure expected by Penguin
        const userData = {
            id: npcData.id,
            username: name,
            displayName: name,
            x: npcData.x || 400,
            y: npcData.y || 400,
            frame: npcData.frame || 1,
            color: npcData.color || 1,
            head: npcData.head || 0,
            face: npcData.face || 0,
            neck: npcData.neck || 0,
            body: npcData.body || 0,
            hand: npcData.hand || 0,
            feet: npcData.feet || 0,
            flag: npcData.flag || 0,
            photo: npcData.photo || 0,
            isNPC: true
        }

        super(userData, room)

        // Store NPC-specific data
        this.npcId = npcData.npcId || npcData.id
        this.internalName = npcData.internalName
        this.description = npcData.description
        this.dialog = npcData.dialog || []
        this.quests = npcData.quests || []
    }

    /**
     * Override the click handler to open NPC dialog instead of player card
     */
    onClick() {
        // Send NPC interaction request to server
        this.network.send('npc_interact', { npcId: this.npcId })
    }

    /**
     * Make the NPC say something
     * @param {string} text - Text to display in speech balloon
     */
    say(text) {
        if (this.interface?.main?.balloonFactory) {
            this.interface.main.balloonFactory.showTextBalloon(
                this, 
                text, 
                false, // not safe chat 
                false, // not emote
                false, // not tour
                false, // not mascot
                true   // is NPC
            )
        }
    }

    /**
     * Make the NPC show an emote
     * @param {number} emote - Emote ID
     */
    emote(emote) {
        if (this.interface?.main?.balloonFactory) {
            this.interface.main.balloonFactory.showEmoteBalloon(
                this, 
                emote, 
                false, // not tour
                false, // not mascot
                true   // is NPC
            )
        }
    }

    /**
     * Move the NPC to a position
     * @param {number} x - Target X position
     * @param {number} y - Target Y position
     * @param {number} frame - Optional frame to play after move
     */
    moveTo(x, y, frame = null) {
        if (frame) {
            this.afterMove = () => this.playFrame(frame)
        }

        // Use parent move method
        this.move(x, y)
    }

    /**
     * Set NPC appearance item
     * @param {number} itemId - Item ID
     * @param {string} slot - Slot name (head, face, body, etc.)
     */
    setItem(itemId, slot) {
        this.update(itemId, slot)
    }

    /**
     * Helper methods for changing appearance
     */
    setHead(item) { this.update(item, 'head') }
    setFace(item) { this.update(item, 'face') }
    setNeck(item) { this.update(item, 'neck') }
    setBody(item) { this.update(item, 'body') }
    setHand(item) { this.update(item, 'hand') }
    setFeet(item) { this.update(item, 'feet') }
    setFlag(item) { this.update(item, 'flag') }
    setPhoto(item) { this.update(item, 'photo') }
    setColor(color) { this.update(color, 'color') }

    /**
     * Check if this NPC has a specific quest
     * @param {string} questId - Quest ID to check
     * @returns {boolean}
     */
    hasQuest(questId) {
        return this.quests.some(q => q.id === questId)
    }

    /**
     * Get a dialog by ID
     * @param {string} dialogId - Dialog ID
     * @returns {Object|null}
     */
    getDialog(dialogId) {
        return this.dialog.find(d => d.id === dialogId)
    }

    /**
     * Get a quest by ID
     * @param {string} questId - Quest ID
     * @returns {Object|null}
     */
    getQuest(questId) {
        return this.quests.find(q => q.id === questId)
    }

}
